import { find, isFinite, isNil, sum, uniq } from 'lodash';

import { tokenFromVizID } from '@mode/charts/domain';
import { EmbedActionType, EmbedActions, EmbedReportState } from '@mode/reports/sandbox/contract';
import { ColumnSettings } from '@mode/legacy/data-access-model-js';
import { ModelState } from '@mode/legacy/types-app-state';
import { isBuilderMode, isDesignerMode, isPhantomMode, isReportMode } from '@mode/shared/util-js';
import { COLUMN_NUMBER_DATA_TYPE_IDS, TREND_PRECISION_TYPES } from '../../../../common/constants';
import * as format from '../../../../lib/axel/format';
import {
  number as numberFormat,
  numberTruncate as numberTruncateFormat,
  percentage as percentageFormat,
  percentageTruncate as percentageTruncateFormat,
} from '../../../../lib/axel/format';
import { StraightModel } from '../../../../lib/axel/model/straight.model';
import { AxelType } from '../../../../lib/axel/type';
import { median } from '../../../../lib/axel/util';
import { getDatasetModel } from '../../../../lib/dataset-helpers';
import ColumnOptionsService from '../../../../services/column-options-service';
import FormatOptionsService from '../../../../services/format-options-service';
import ReportLinkService from '../../../../services/report-link-service';

declare type TrendDirection = 'positive' | 'negative' | 'neutral';

interface BigNumberViewOptions {
  aggregator?: 'last_value' | 'min_value' | 'max_value' | 'average' | 'count' | 'count_distinct' | 'median' | 'sum';
  column?: string;
  columns?: ColumnSettings;
  referencePoint?: 'previous' | 'next' | 'custom';
  referencePointCustom?: string;
  sortColumn?: string;
  sortOrder?: 'asc' | 'desc';
  trend?: 'change_rate' | 'total_change' | 'none';
  trendLabel?: string;
  trendPrecision?: 'auto' | number;
  colorScaleID?: string;
  colors?: string[];
  colorPalette?: string;
}

const TREND_TYPE_TO_FORMAT_MAP = {
  change_rate: 'percentage',
  total_change: 'number',
};

export default class BigNumberController {
  static $inject = [
    '$element',
    '$window',
    '$scope',
    '$timeout',
    'ColumnOptionsService',
    'FormatOptionsService',
    'ReportLinkService',
  ];

  static UNDEFINED_TREND = '--%';
  static UNDEFINED_AGGREGATE = '--';
  static DISPLAY_ROW_LIMIT = 10000;

  view: any;
  dataset: EmbedReportState.EmbedDataset | ModelState;
  chartID: string;
  token: string;
  onAction: (args: { $action: EmbedActions }) => void;
  onEdit: any;
  onRenderStart: any;
  onRenderEnd: (args: { $visualizationRendered: boolean }) => void;

  data: StraightModel;

  reportRenderToken: string;

  isClipboardEnabled = false; // handled by clipboard directive

  cachedFormattedAggregate: number | string;
  cachedTrendDirection: TrendDirection;
  cachedChangeRate: number | string;

  constructor(
    private $element,
    private $window,
    private $scope,
    private $timeout: angular.ITimeoutService,
    private cos: ColumnOptionsService,
    private fos: FormatOptionsService,
    private reportLinkService: ReportLinkService
  ) {}

  $onInit = () => {
    this.token = tokenFromVizID(this.chartID);

    this.onRenderStart({
      $vizID: this.token,
    });

    if (this.view) {
      this.initViewModel();
    }

    // deep watch view options for changes
    this.$scope.$watch(
      'ctrl.view',
      (newVal, oldVal) => {
        this.clearCache();
      },
      true
    );
  };

  $onChanges = (changed) => {
    if (changed.view || changed.dataset || changed.chartID) {
      this.clearCache();
    }
  };

  copySuccess() {
    this.onAction({
      $action: {
        type: EmbedActionType.ShowAlert,
        success: true,
        message: 'Link to big number copied successfully',
      },
    });
  }

  initViewModel() {
    if (this.dataset) {
      this.data = getDatasetModel(this.dataset, {
        rowLimit: BigNumberController.DISPLAY_ROW_LIMIT,
      });
      this.$timeout(() => {
        this.onRenderEnd({
          $visualizationRendered: true,
        });
      }, 1);
    }
  }

  clearCache() {
    this.cachedFormattedAggregate = null;
    this.cachedTrendDirection = null;
    this.cachedChangeRate = null;
    this.initViewModel();
  }

  isBuilderMode() {
    return isBuilderMode(this.$window.document.body);
  }

  isDesignerMode() {
    return isDesignerMode(this.$window.document.body);
  }

  isReportMode() {
    return isReportMode(this.$window.document.body);
  }

  isPreviewMode() {
    return isBuilderMode(this.$window.document.body);
  }

  isPhantomMode() {
    return isPhantomMode(this.$window);
  }

  linkTo($event, type) {
    if (this.reportLinkService.isLinkable()) {
      $event.preventDefault();
      $event.stopPropagation();
      this.reportLinkService.linkToPage(
        type === 'lightbox' ? { id: this.chartID, view: this.view } : this.queryToken(),
        type
      );
    }
  }

  dataState(pattern) {
    const state = this.dataset && this.dataset.state;

    if (state && pattern) {
      return !!~state.search(pattern);
    } else {
      return state;
    }
  }

  getReportDataURL() {
    const data = this.getQueryData();
    return (data && data['reportDataUrl']) || '';
  }

  getReportQueryURL() {
    const data = this.getQueryData();
    return (data && data['reportQueryUrl']) || '';
  }

  getReportVizURL() {
    return (this.view && this.view._vizReportRunLink) || '';
  }

  getQueryData() {
    return this.dataset;
  }

  titleEditDisabled() {
    return this.isReportMode() && !this.isPreviewMode();
  }

  displayTitle(title = '') {
    return (this.isReportMode() && title.length) || this.isDesignerMode() || this.isPreviewMode();
  }

  chartTitle() {
    return this.view.chartTitle;
  }

  chartDescription() {
    return this.view.chartDescription;
  }

  trendLabel() {
    return this.bigNumberViewOptions()['trendLabel'];
  }

  colorScaleName() {
    return this.bigNumberViewOptions()['colorScaleID'];
  }

  broadcastEdit() {
    this.onEdit({ $id: this.chartID, $view: this.view });
  }

  aggregate() {
    const { aggregator } = this.bigNumberViewOptions();
    const isValid = (v) => !isNil(v) && !AxelType.isDirty(v);
    const data = this.selectedColumnData();
    const dataFiltered = data.filter(isValid);
    const asc = (a, b) => a - b;
    const desc = (a, b) => b - a;
    const aggregators = {
      average: () => dataFiltered.reduce((accu, prev) => parseFloat(accu) + parseFloat(prev), 0) / dataFiltered.length,
      last_value: () => data[data.length - 1],
      min_value: () => dataFiltered.sort(asc)[0],
      max_value: () => dataFiltered.sort(desc)[0],
      count: () => dataFiltered.length,
      count_distinct: () => uniq(dataFiltered).length,
      median: () => median(dataFiltered),
      sum: () => sum(dataFiltered),
    };

    return aggregators[aggregator]();
  }

  queryToken() {
    return this.dataset && this.dataset.query_token;
  }

  selectedColumnData(): Array<any> {
    const { column: colKey, sortColumn, sortOrder } = this.bigNumberViewOptions();
    const data: StraightModel = this.data;
    const sortColumnIndex = data.cols().indexOf(sortColumn);

    if (sortColumnIndex !== -1) {
      data.sort(sortColumnIndex, sortOrder);
    }

    return data.rows().map((rowKey) => data.tree[rowKey][colKey].value().valueOf());
  }

  bigNumberViewOptions(): BigNumberViewOptions {
    return this.view.bigNumber || {};
  }

  trendAvailable() {
    const { trend, aggregator } = this.bigNumberViewOptions();

    return trend !== 'none' && aggregator === 'last_value';
  }

  pastPresentValues() {
    const { referencePoint, referencePointCustom } = this.bigNumberViewOptions();
    const data = this.selectedColumnData();
    const lastIndex = data.length - 1;
    const pastValues = {
      custom: data[lastIndex - (parseInt(referencePointCustom) || 0)],
      previous: data[lastIndex - 1],
      first: data[0],
    };

    return {
      present: this.aggregate(),
      past: isFinite(pastValues[referencePoint]) ? pastValues[referencePoint] : pastValues['previous'],
    };
  }

  calculateTrend() {
    const { trend, referencePoint, referencePointCustom } = this.bigNumberViewOptions();
    const { past, present } = this.pastPresentValues();
    const resolveTrendType = (type) => (trendMap[type] || trendMap['auto'])();
    const trendMap = {
      auto: () => trendMap['percentage'](),
      percentage: () => changeRate,
      number: () => changeDifference,
    };
    const trendFormat = TREND_TYPE_TO_FORMAT_MAP[trend];

    if (trend === 'none') {
      return NaN;
    }
    if (referencePoint === 'custom' && isNaN(parseInt(referencePointCustom))) {
      return NaN;
    }
    return resolveTrendType(trendFormat)(present, past);
  }

  formattedAggregate() {
    if (this.invalidColumnSelected()) {
      return BigNumberController.UNDEFINED_AGGREGATE;
    }
    if (this.cachedFormattedAggregate) {
      return this.cachedFormattedAggregate;
    }

    let formatter;
    const { column: colKey, columns } = this.bigNumberViewOptions();
    const colType = this.cos.columnType(columns, colKey) || 'automatic';
    const moneyFormatters = this.fos.getPresetFormatters(columns);
    const aggregate = this.aggregate();

    if (AxelType.isDirty(aggregate)) {
      return (this.cachedFormattedAggregate = BigNumberController.UNDEFINED_AGGREGATE);
    }

    if (colType === 'automatic') {
      formatter = format.numberTruncate();
    } else {
      formatter = this.fos.getFormatter(columns, colKey, aggregate, colType, moneyFormatters);
    }

    return (this.cachedFormattedAggregate = formatter(aggregate));
  }

  font() {
    return '"Graphik", Helvetica, Arial, sans-serif';
  }

  formattedChangeRate() {
    if (this.invalidColumnSelected()) {
      return BigNumberController.UNDEFINED_TREND;
    }
    if (this.cachedChangeRate) {
      return this.cachedChangeRate;
    }

    const { trend, trendPrecision } = this.bigNumberViewOptions();
    let result: string | number = BigNumberController.UNDEFINED_TREND;
    const trendValue = this.calculateTrend();
    const precisionIDByName = (name) => find(TREND_PRECISION_TYPES, { name })['id'];
    const smartPrecision = (target: number) => precisionIDByName(target % 1 ? '0.01' : '1');
    const resolveFormatter = (type) => formatters[type] || formatters['auto'];
    const resolveTruncatedFormatter = (type) => truncFormatters[type] || truncFormatters['auto'];
    const resolvePrecision = (precision: number | string, fallback: Function) =>
      isFinite(precision) && Number(precision) >= 0 ? precision : fallback();
    const truncFormatters = {
      auto: () => truncFormatters['percentage'](),
      percentage: () => percentageTruncateFormat(),
      number: () => numberTruncateFormat(),
    };
    const formatters = {
      auto: (opts) => formatters['percentage'](opts),
      percentage: (opts) => percentageFormat(null, opts),
      number: (opts) => numberFormat(null, opts),
    };
    const trendFormat = TREND_TYPE_TO_FORMAT_MAP[trend];
    const precision = resolvePrecision(trendPrecision, () => smartPrecision(trendValue));
    const format =
      Number(trendPrecision) >= 0 && trendPrecision !== 'auto'
        ? resolveFormatter(trendFormat)({ precision })
        : resolveTruncatedFormatter(trendFormat)();

    if (isFinite(trendValue)) {
      result = format(Math.abs(trendValue));
    }

    return (this.cachedChangeRate = result);
  }

  invalidColumnSelected() {
    const { column } = this.bigNumberViewOptions();
    const columnNotFound = this.data.cols().indexOf(column) === -1;
    const columnNotNumerical = !isNumberColumn(find(this.dataset && this.dataset.columns, { name: column }));

    return columnNotFound || columnNotNumerical;
  }

  numericalColumnsAvailable() {
    return this.dataset && this.dataset.columns.filter(isNumberColumn).length > 0;
  }

  /**
   * Is Empty State In Designer
   *
   * Derivative state that depends on the absence of numerical columns and
   * the designer mode context.
   * @returns {boolean}
   */
  isEmptyStateInDesigner() {
    return !this.numericalColumnsAvailable() && this.isDesignerMode();
  }

  /**
   * Trend Direction
   * @todo save/memoize this value
   * @returns {string}
   */
  trendDirection(): TrendDirection {
    // don't bother if invalid column is selected
    if (this.invalidColumnSelected()) {
      return;
    }
    if (this.cachedTrendDirection) {
      return this.cachedTrendDirection;
    }

    let result: TrendDirection = 'negative';
    const rate = this.calculateTrend();

    if (!isFinite(rate) || rate === 0) {
      result = 'neutral';
    } else if (rate > 0) {
      result = 'positive';
    }

    return (this.cachedTrendDirection = result);
  }

  trendArrowStyle() {
    const color = this.trendColorFromPalette();
    if (color) {
      switch (this.trendDirection()) {
        case 'positive':
          return 'border-bottom-color: ' + this.trendColorFromPalette();
        case 'negative':
          return 'border-top-color: ' + this.trendColorFromPalette();
        case 'neutral':
          return 'display: none;';
      }
    } else {
      return '';
    }
  }

  trendColorFromPalette() {
    const colors = this.bigNumberViewOptions()['colors'];
    if (colors) {
      switch (this.trendDirection()) {
        case 'positive':
          return colors[2];
        case 'negative':
          return colors[0];
        case 'neutral':
          return colors[1];
      }
    }
  }

  updateChartTitle(text) {
    this.view.chartTitle = text;
    this.broadcastEdit();
  }

  updateChartDescription(text) {
    this.view.chartDescription = text;
    this.broadcastEdit();
  }

  exceedsRowLimit() {
    return this.dataset && this.dataset.count > BigNumberController.DISPLAY_ROW_LIMIT;
  }

  getRowLimit() {
    return BigNumberController.DISPLAY_ROW_LIMIT;
  }
}

function changeRate(present, past) {
  return (present - past) / past;
}

function changeDifference(present, past) {
  return present - past;
}

function isNumberColumn(column: any = { type: '' }) {
  return COLUMN_NUMBER_DATA_TYPE_IDS.indexOf(column.type) >= 0;
}
